import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import useStores from '@lib/hooks/useStores';
import { Menu, Progress } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import { IUserStore } from '@stores/userStore';
import { scMixinPseudoBlock, scMixinEllipsis } from '@utils/emotion/mixins';

const ProcessingVideosDropdown: React.FC = observer(() => {
  const { userStore } = useStores<{ userStore: IUserStore }>();
  const { t } = useTranslation();

  return (
    <DropdownContainer>
      <div className="title">
        <h2 className="text">{t('header.action.progress.desc')}</h2>
      </div>
      <Menu
        mode="inline"
        items={userStore.currentlyProcessingVideoIds.map((id) => {
          const video = userStore.currentlyProcessingVideoDatas[id];

          if (!video) {
            return {
              key: id,
              label: (
                <PendingVideoItemBlock>
                  <LoadingOutlined style={{ fontSize: 36 }} spin />
                </PendingVideoItemBlock>
              ),
            };
          }

          const {
            title,
            thumbnailUrl,
            chapters,
            progress: chapterProgress,
            currentProcessingChapterIdx,
          } = video;

          let totalDuration = 0;
          let progress = 0;

          chapters.forEach((chapter) => (totalDuration += chapter.endTime - chapter.startTime));
          chapters.forEach((chapter) => {
            if (chapter.chapterIdx === currentProcessingChapterIdx) {
              // current chapter
              progress += Math.ceil(
                (((chapter.endTime - chapter.startTime) * (chapterProgress / 100)) /
                  totalDuration) *
                  100,
              );
            } else if (chapter.chapterIdx < currentProcessingChapterIdx) {
              // previous chapters
              progress += Math.ceil(
                ((chapter.endTime - chapter.startTime * 1) / totalDuration) * 100,
              );
            }
          });

          const isComplete = progress > 99;

          return {
            key: id,
            label: (
              <VideoItemBlock>
                <Link
                  to={`/video/${id}/editor/chapter`}
                  onClick={() => {
                    if (isComplete) userStore.ejectProcessingVideo(id);
                  }}
                  className="inner"
                >
                  <div className="thumbnail">
                    {thumbnailUrl && <img src={thumbnailUrl} alt={`${title} Thumbnail`} />}
                    <div className="progress">
                      <Progress
                        type="circle"
                        percent={progress}
                        strokeColor={isComplete ? '#52c41a' : '#5100ff'}
                        width={48}
                        className="progress-bar"
                      />
                    </div>
                  </div>
                  <div className="info">
                    <div className="primary-info">
                      <strong className="title">{video.title}</strong>
                    </div>
                    <div className="sub-info">
                      <div className="status">
                        {progress === 0
                          ? t('common.standby')
                          : isComplete
                          ? t('common.completed')
                          : `${t('common.analyzing')} (${t('header.action.progress.chapter')} ${
                              currentProcessingChapterIdx + 1
                            }/${chapters.length})`}
                      </div>
                    </div>
                  </div>
                </Link>
              </VideoItemBlock>
            ),
          };
        })}
      />
    </DropdownContainer>
  );
});

const DropdownContainer = styled.div`
  position: relative;
  top: 13px;
  width: 360px;
  padding: 8px 0 !important;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1) !important;
  background-color: #fff;

  & > .title {
    padding: 0 16px 10px;

    .text {
      margin: 0;
      font-weight: bold;
      font-size: 12px;
      line-height: 14px;
    }
  }

  .ant-menu-item {
    margin: 0 !important;
    padding: 0 !important;
    height: auto !important;

    &:first-of-type > div {
      border-top: none !important;
    }
  }
`;

const VideoItemBlock = styled.div`
  border-top: 1px solid #e9e9e9;

  .inner {
    display: flex;
    flex-direction: row;
    overflow: hidden;
    padding: 8px 16px;
    transition: background-color 200ms ease;
    color: #555;

    &:hover {
      background-color: #f9f9f9;
    }
  }

  .thumbnail {
    position: relative;
    width: 114px;
    height: 56px;

    &::before {
      ${scMixinPseudoBlock};
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 0;
      background-color: rgba(0, 0, 0, 0.4);
    }

    img {
      display: block;
      height: 100%;
    }

    .progress {
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 1;
      width: 48px;
      height: 48px;
      margin: -24px 0 0 -24px;
    }

    .ant-progress-text {
      color: #fff;

      span {
        margin: 0 auto;
      }
    }
  }

  .info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    padding-left: 16px;

    .sub-info {
      margin-top: 8px;
    }

    .title {
      ${scMixinEllipsis};
      display: block;
      line-height: 1.25;
    }

    .status {
      font-size: 11px;
      line-height: 1.25;
    }
  }
`;

const PendingVideoItemBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80px;
`;

export default ProcessingVideosDropdown;
